import { useEffect, useState } from 'react';
import { enablePush, isIos, isIosNonSafari, isStandalone, pushStatus } from '../lib/push';
import { useSession } from '../state/session';

const DISMISS_KEY = 'huddle:install-dismissed';

/**
 * The notifications on-ramp, and on iOS specifically the Add to Home Screen
 * walkthrough build.txt asks for.
 *
 * iOS Safari does not expose Web Push at all in a browser tab — no permission
 * prompt to show, no API to call. The only route to a notification on an
 * iPhone is: Share → Add to Home Screen → open the installed app → allow
 * notifications. Staff will not discover that on their own, so this walks them
 * through it with the actual glyph they are looking for.
 *
 * Dismissal is remembered, but only until the next sign-in on a new device;
 * being unreachable is a real operational problem and worth one more nudge.
 */
export default function InstallPrompt() {
  const { me, refresh } = useSession();
  const [status, setStatus] = useState(pushStatus);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === '1');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setStatus(pushStatus());
  }, [me.pushEnabled]);

  if (dismissed || me.pushEnabled) return null;
  if (status === 'granted' || status === 'unsupported') return null;

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  }

  async function turnOn() {
    setBusy(true);
    setError(null);
    const result = await enablePush().catch((e: unknown) => ({
      ok: false as const,
      reason: e instanceof Error ? e.message : 'Could not turn on notifications.',
    }));
    setBusy(false);
    if (result.ok) {
      await refresh();
      setStatus(pushStatus());
    } else {
      setError(result.reason ?? 'Could not turn on notifications.');
    }
  }

  // iOS, but not in Safari. Chrome/Firefox/Edge on iOS cannot produce a
  // push-capable home-screen app, and their Share sheet has no "Add to Home
  // Screen" — so the Safari instructions below would send someone hunting for
  // a menu item that isn't there. Get them into Safari first.
  if (isIosNonSafari() && !isStandalone()) {
    return (
      <Card onDismiss={dismiss}>
        <p className="font-semibold text-zinc-900">Open this in Safari</p>
        <p className="mt-1 text-sm text-zinc-600">
          Notifications on iPhone only work from a home-screen app, and only Safari can create one.
          This browser can't.
        </p>
        <ol className="mt-3 space-y-2 text-sm text-zinc-700">
          <Step n={1}>
            Copy this address: <span className="font-medium">huddle.vtsapp.net</span>
          </Step>
          <Step n={2}>Open Safari and paste it in</Step>
          <Step n={3}>
            Tap <span className="font-medium">Share</span> &rarr;{' '}
            <span className="font-medium">Add to Home Screen</span>
          </Step>
          <Step n={4}>
            Open The Huddle from your home screen and sign in with an emailed{' '}
            <span className="font-medium">code</span> — the link won't sign the app in
          </Step>
        </ol>
        <button
          type="button"
          onClick={() => void navigator.clipboard?.writeText('https://huddle.vtsapp.net')}
          className="mt-3 w-full rounded-xl bg-zinc-100 py-2.5 text-sm font-semibold text-zinc-700"
        >
          Copy the address
        </button>
      </Card>
    );
  }

  // iOS Safari in a browser tab: instructions, not a button, because there is
  // no API to call until they install.
  if (isIos() && !isStandalone()) {
    return (
      <Card onDismiss={dismiss}>
        <p className="font-semibold text-zinc-900">Turn on notifications</p>
        <p className="mt-1 text-sm text-zinc-600">
          On iPhone, The Huddle has to be on your home screen before it can notify you. Takes ten
          seconds:
        </p>
        <ol className="mt-3 space-y-2 text-sm text-zinc-700">
          <Step n={1}>
            Tap{' '}
            <span className="mx-0.5 inline-flex items-center gap-1 rounded bg-zinc-100 px-1.5 py-0.5 align-middle font-medium">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-3.5 w-3.5"
              >
                <path d="M12 16V4m0 0L8 8m4-4 4 4" />
                <path d="M4 14v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4" />
              </svg>
              Share
            </span>{' '}
            at the bottom of Safari
          </Step>
          <Step n={2}>
            Scroll down and tap <span className="font-medium">Add to Home Screen</span>
          </Step>
          {/* Not "come back here" — the installed app has its own cookie
              container, so it opens logged out and needs its own sign-in. */}
          <Step n={3}>
            Open The Huddle from your home screen — it'll ask you to sign in again, with a{' '}
            <span className="font-medium">code</span> we email you
          </Step>
        </ol>
        <p className="mt-3 text-xs leading-relaxed text-zinc-500">
          The home-screen app signs in separately from Safari. That's an iPhone thing, not a bug.
        </p>
      </Card>
    );
  }

  return (
    <Card onDismiss={dismiss}>
      <p className="font-semibold text-zinc-900">Turn on notifications</p>
      <p className="mt-1 text-sm text-zinc-600">
        {status === 'denied'
          ? 'Notifications are blocked for this site. Turn them back on in your browser settings, then reload.'
          : "So you know when there's a shift change or the walk-in goes down."}
      </p>
      {status !== 'denied' && (
        <button
          type="button"
          onClick={turnOn}
          disabled={busy}
          className="mt-3 w-full rounded-xl bg-brand-600 py-2.5 text-sm font-semibold text-white active:bg-brand-700 disabled:opacity-50"
        >
          {busy ? 'Turning on…' : 'Turn on notifications'}
        </button>
      )}
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </Card>
  );
}

function Card({ children, onDismiss }: { children: React.ReactNode; onDismiss: () => void }) {
  return (
    <div className="relative m-3 rounded-2xl border border-brand-100 bg-brand-50/60 p-4">
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="absolute top-2.5 right-2.5 flex h-8 w-8 items-center justify-center rounded-full text-zinc-400 active:bg-zinc-200/50"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          className="h-4 w-4"
        >
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
      {children}
    </div>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-2.5">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-600 text-[11px] font-bold text-white">
        {n}
      </span>
      <span className="leading-relaxed">{children}</span>
    </li>
  );
}
